import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { AlertCircle, GraduationCap, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Button,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  StatusPill,
  cn,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import { ApiError, type Parent, type ParentStudentRef, type StudentStatus } from "@eduviaos/shared";
import { useParentStudents } from "@/hooks/useParents";

/**
 * Phase J3.c — Parent → linked students drawer.
 *
 * Read-only side panel that surfaces every Student doc whose
 * `parent_id` points at the selected Parent. Powered by the existing
 * `useParentStudents` query (Iron Rule #1 verified — `GET /api/parents/
 * {parent_id}/students` returns `{parent_id, students[], count}`).
 *
 * Why a separate sheet vs. inline expansion?
 *  - The parents row is already 7-column tabular; an inline expansion
 *    would push the table to ~10 cells and break the sticky-thead UX.
 *  - The drawer pattern matches `<ParentEditorSheet>` and the
 *    enquiry / student drawers — one mental model for IA admins.
 *  - Keeps the drawer focused on a single household — clicking a
 *    student row deep-links to the Students tab where the full row,
 *    drawer, and actions live.
 *
 * Permissions: read-only — `view_students` is enough (the parent list
 * itself requires it). No write affordances live here in J3.c. Linking
 * a student to a parent happens via `<StudentEditorSheet>` (Students
 * tab) — that flow already exists from J1.b.
 */

const STATUS_VARIANT: Record<StudentStatus, StatusPillStatus> = {
  active: "active",
  paused: "invited",
  graduated: "archived",
  withdrawn: "archived",
  transferred: "archived",
};

const STATUS_LABEL: Record<StudentStatus, string> = {
  active: "Active",
  paused: "Paused",
  graduated: "Graduated",
  withdrawn: "Withdrawn",
  transferred: "Transferred",
};

function gradeSectionLabel(student: ParentStudentRef): string | null {
  const parts = [student.grade, student.section].filter((p): p is string => Boolean(p));
  return parts.length === 0 ? null : parts.join(" · ");
}

export interface ParentStudentsSheetProps {
  /**
   * The parent whose linked students should be rendered. Pass `null`
   * (or omit) to keep the sheet closed — `open` alone is not enough
   * because the query needs a real id.
   */
  parent: Parent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ParentStudentsSheet({ parent, open, onOpenChange }: ParentStudentsSheetProps) {
  const reduceMotion = useReducedMotion();
  const query = useParentStudents(open && parent ? parent.id : null);
  const { data, isLoading, isError, error } = query;

  // Body content is the only thing that swaps when status changes;
  // we hold header/footer steady so the drawer doesn't visually pop.
  const body = (() => {
    if (!parent) return null;
    if (isLoading) {
      return (
        <div
          className="flex flex-col items-center justify-center gap-2 py-12"
          data-testid="parent-students-loading"
        >
          <Loader2 aria-hidden="true" className="text-muted-foreground size-5 animate-spin" />
          <p className="text-muted-foreground text-xs">Loading linked students…</p>
        </div>
      );
    }
    if (isError) {
      const friendly =
        error instanceof ApiError
          ? error.message || "Couldn't load linked students."
          : error instanceof Error && error.message
            ? error.message
            : "Couldn't load linked students.";
      return (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
          data-testid="parent-students-error"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">{friendly}</span>
        </div>
      );
    }

    const students = data?.students ?? [];
    if (students.length === 0) {
      return (
        <div
          className="border-border/60 bg-muted/40 flex flex-col items-center justify-center gap-2 rounded-md border border-dashed px-4 py-10 text-center"
          data-testid="parent-students-empty"
        >
          <GraduationCap aria-hidden="true" className="text-muted-foreground size-5" />
          <p className="text-sm font-medium">No students linked yet</p>
          <p className="text-muted-foreground max-w-xs text-xs">
            Open a Student record and pick {parent.name.split(" ")[0]} as the parent / guardian to
            start the household.
          </p>
          <Button asChild size="sm" variant="outline" className="mt-2">
            <Link to="/people?tab=students" onClick={() => onOpenChange(false)}>
              Go to Students
            </Link>
          </Button>
        </div>
      );
    }

    return (
      <ul
        className="divide-border/60 divide-y rounded-md border"
        data-testid="parent-students-list"
      >
        {students.map((s) => {
          const gs = gradeSectionLabel(s);
          return (
            <li
              key={s.id}
              data-testid={`parent-student-row-${s.id}`}
              className="hover:bg-muted/40 flex items-center gap-3 px-3 py-2.5 transition-colors duration-100"
            >
              <div className="min-w-0 flex-1">
                <Link
                  to={`/people?tab=students&parent_id=${parent.id}`}
                  onClick={() => onOpenChange(false)}
                  className="block truncate text-sm font-medium hover:underline"
                >
                  {s.name}
                </Link>
                <p className="text-muted-foreground truncate text-xs">
                  {s.admission_number ? (
                    <span className="tabular-nums">{s.admission_number}</span>
                  ) : (
                    <span className="italic">No admission #</span>
                  )}
                  {gs ? <span aria-hidden="true"> · </span> : null}
                  {gs}
                </p>
              </div>
              <StatusPill
                status={STATUS_VARIANT[s.status] ?? "inactive"}
                label={STATUS_LABEL[s.status] ?? s.status}
              />
            </li>
          );
        })}
      </ul>
    );
  })();

  // Count for the header subtitle. Falls back to the doc-known count
  // while loading.
  const liveCount = data?.count ?? 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col sm:max-w-md"
        data-testid="parent-students-sheet"
      >
        <SheetHeader>
          <SheetTitle>Linked students</SheetTitle>
          <SheetDescription>
            {parent ? (
              <>
                Students currently linked to{" "}
                <span className="text-foreground font-medium">{parent.name}</span>
                {data ? (
                  <span className="text-muted-foreground tabular-nums">
                    {" "}
                    · {liveCount.toLocaleString()} {liveCount === 1 ? "student" : "students"}
                  </span>
                ) : null}
                .
              </>
            ) : (
              "Select a parent to see their household."
            )}
          </SheetDescription>
        </SheetHeader>

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={isLoading ? "loading" : isError ? "error" : `loaded-${liveCount}`}
            initial={reduceMotion ? false : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
            transition={{ duration: 0.16, ease: [0.23, 1, 0.32, 1] }}
            className={cn("flex-1 overflow-y-auto py-4")}
          >
            {body}
          </motion.div>
        </AnimatePresence>

        <div className="border-border/60 border-t pt-3">
          <SheetClose asChild>
            <Button variant="outline" size="sm" className="w-full">
              Close
            </Button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
}
